import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaEye, FaTimes, FaExternalLinkAlt, FaChevronLeft, FaChevronRight, FaNewspaper, FaClock, FaUserTie } from 'react-icons/fa';
import DeleteButton from '../editors/DeleteButton';

interface NewsArticle {
  id: number;
  titleAr: string;
  titleEn: string;
  excerptAr: string;
  excerptEn: string;
  contentAr: string;
  contentEn: string;
  images: string[];
  dateAr: string;
  dateEn: string;
  authorAr: string;
  authorEn: string;
  views: number;
  categoryAr: string;
  categoryEn: string;
  readTimeAr: string;
  readTimeEn: string;
  tagsAr: string[];
  tagsEn: string[];
}

interface NewsSectionProps {
  language?: 'ar' | 'en';
  articles?: NewsArticle[];
  onDeleteArticle?: (index: number) => void;
}

const NewsSection: React.FC<NewsSectionProps> = ({ 
  language = 'ar',
  articles = [
    {
      id: 1,
      titleAr: 'شركة مكة البركة تفتتح مشروع برج الأعمال التجارية في القاهرة',
      titleEn: 'Mecca El Baraka Opens Commercial Business Tower Project in Cairo',
      excerptAr: 'تم افتتاح مشروع برج الأعمال التجارية بنجاح في قلب العاصمة القاهرة، وهو أحد أكبر المشاريع التجارية في المنطقة.',
      excerptEn: 'The commercial business tower project was successfully opened in the heart of Cairo, one of the largest commercial projects in the region.',
      contentAr: 'تم افتتاح مشروع برج الأعمال التجارية بنجاح في قلب العاصمة القاهرة، وهو أحد أكبر المشاريع التجارية في المنطقة. يضم البرج 25 طابقاً من المكاتب التجارية والمحلات الفاخرة، مع مرافق متكاملة تشمل موقف سيارات متعدد الطوابق ومركز تجاري حديث. تم تنفيذ المشروع بأحدث التقنيات الهندسية ومواد البناء عالية الجودة، مما يجعله نموذجاً للتميز في مجال البناء والتشييد.',
      contentEn: 'The commercial business tower project was successfully opened in the heart of Cairo, one of the largest commercial projects in the region. The tower includes 25 floors of commercial offices and luxury shops, with integrated facilities including a multi-story parking garage and a modern shopping center. The project was implemented with the latest engineering technologies and high-quality building materials, making it a model of excellence in the field of construction and contracting.',
      images: [
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
        'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80'
      ],
      dateAr: '15 مارس 2024',
      dateEn: 'March 15, 2024',
      authorAr: 'فريق التحرير',
      authorEn: 'Editorial Team',
      views: 1250,
      categoryAr: 'مشاريع',
      categoryEn: 'Projects',
      readTimeAr: '5 دقائق قراءة',
      readTimeEn: '5 min read',
      tagsAr: ['مشاريع تجارية', 'القاهرة', 'برج أعمال'],
      tagsEn: ['Commercial Projects', 'Cairo', 'Business Tower']
    },
    {
      id: 2,
      titleAr: 'إطلاق تقنيات جديدة في مجال البناء المستدام',
      titleEn: 'Launching New Technologies in Sustainable Construction',
      excerptAr: 'تستثمر شركة مكة البركة في تقنيات البناء المستدام لتقليل البصمة الكربونية وتحسين كفاءة الطاقة.',
      excerptEn: 'Mecca El Baraka For Construction invests in sustainable construction technologies to reduce carbon footprint and improve energy efficiency.',
      contentAr: 'تستثمر شركة مكة البركة في تقنيات البناء المستدام لتقليل البصمة الكربونية وتحسين كفاءة الطاقة. تشمل هذه التقنيات استخدام مواد بناء صديقة للبيئة، وأنظمة طاقة شمسية متطورة، وتقنيات عزل حراري حديثة. تم تطبيق هذه التقنيات في عدة مشاريع تجريبية أثبتت نجاحها في تقليل استهلاك الطاقة بنسبة تصل إلى 40% مقارنة بالمباني التقليدية.',
      contentEn: 'Mecca El Baraka For Construction invests in sustainable construction technologies to reduce carbon footprint and improve energy efficiency. These technologies include the use of environmentally friendly building materials, advanced solar energy systems, and modern thermal insulation technologies. These technologies have been applied in several pilot projects that have proven successful in reducing energy consumption by up to 40% compared to traditional buildings.',
      images: [
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
        'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80'
      ],
      dateAr: '10 مارس 2024',
      dateEn: 'March 10, 2024',
      authorAr: 'أحمد محمد',
      authorEn: 'Ahmed Mohamed',
      views: 890,
      categoryAr: 'تقنيات',
      categoryEn: 'Technology',
      readTimeAr: '7 دقائق قراءة',
      readTimeEn: '7 min read',
      tagsAr: ['بناء مستدام', 'تقنيات خضراء', 'كفاءة الطاقة'],
      tagsEn: ['Sustainable Building', 'Green Technologies', 'Energy Efficiency']
    },
    {
      id: 3,
      titleAr: 'حصول الشركة على جائزة التميز في البناء',
      titleEn: 'Company Receives Excellence Award in Construction',
      excerptAr: 'حصلت الشركة على جائزة أفضل شركة مقاولات في مصر لعام 2024 تقديراً لتميزها.',
      excerptEn: 'The company received the award for the best contracting company in Egypt for 2024 in recognition of its excellence.',
      contentAr: 'حصلت الشركة على جائزة أفضل شركة مقاولات في مصر لعام 2024 تقديراً لتميزها في تنفيذ المشاريع وجودة العمل. تم تقييم الشركة بناءً على عدة معايير تشمل جودة التنفيذ، الالتزام بالمواعيد، الابتكار في التقنيات المستخدمة، والمسؤولية الاجتماعية. هذه الجائزة تعكس التزام الشركة المستمر بالتميز والابتكار في مجال البناء والتشييد.',
      contentEn: 'The company received the award for the best contracting company in Egypt for 2024 in recognition of its excellence in project execution and work quality. The company was evaluated based on several criteria including execution quality, adherence to deadlines, innovation in technologies used, and social responsibility. This award reflects the company\'s continued commitment to excellence and innovation in the field of construction and contracting.',
      images: [
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
        'https://images.unsplash.com/photo-1545454675-3531b543be5d?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80'
      ],
      dateAr: '5 مارس 2024',
      dateEn: 'March 5, 2024',
      authorAr: 'أحمد محمد',
      authorEn: 'Ahmed Mohamed',
      views: 750,
      categoryAr: 'جوائز',
      categoryEn: 'Awards',
      readTimeAr: '4 دقائق قراءة',
      readTimeEn: '4 min read',
      tagsAr: ['جوائز التميز', '2024', 'أفضل شركة'],
      tagsEn: ['Excellence Awards', '2024', 'Best Company']
    }
  ],
  onDeleteArticle
}) => {
  const isRTL = language === 'ar';
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const news = [
    {
      id: 1,
      titleAr: 'شركة مكة البركة تفتتح مشروع برج الأعمال التجارية في القاهرة',
      titleEn: 'Mecca El Baraka Opens Commercial Business Tower Project in Cairo',
      excerptAr: 'تم افتتاح مشروع برج الأعمال التجارية بنجاح في قلب العاصمة القاهرة، وهو أحد أكبر المشاريع التجارية في المنطقة.',
      excerptEn: 'The commercial business tower project was successfully opened in the heart of Cairo, one of the largest commercial projects in the region.',
      contentAr: 'تم افتتاح مشروع برج الأعمال التجارية بنجاح في قلب العاصمة القاهرة، وهو أحد أكبر المشاريع التجارية في المنطقة. يضم البرج 25 طابقاً من المكاتب التجارية والمحلات الفاخرة، مع مرافق متكاملة تشمل موقف سيارات متعدد الطوابق ومركز تجاري حديث. تم تنفيذ المشروع بأحدث التقنيات الهندسية ومواد البناء عالية الجودة، مما يجعله نموذجاً للتميز في مجال البناء والتشييد.',
      contentEn: 'The commercial business tower project was successfully opened in the heart of Cairo, one of the largest commercial projects in the region. The tower includes 25 floors of commercial offices and luxury shops, with integrated facilities including a multi-story parking garage and a modern shopping center. The project was implemented with the latest engineering technologies and high-quality building materials, making it a model of excellence in the field of construction and contracting.',
      images: [
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
        'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80'
      ],
      dateAr: '15 مارس 2024',
      dateEn: 'March 15, 2024',
      authorAr: 'فريق التحرير',
      authorEn: 'Editorial Team',
      views: 1250,
      categoryAr: 'مشاريع',
      categoryEn: 'Projects',
      readTimeAr: '5 دقائق قراءة',
      readTimeEn: '5 min read',
      tagsAr: ['مشاريع تجارية', 'القاهرة', 'برج أعمال'],
      tagsEn: ['Commercial Projects', 'Cairo', 'Business Tower']
    },
    {
      id: 2,
      titleAr: 'إطلاق تقنيات جديدة في مجال البناء المستدام',
      titleEn: 'Launching New Technologies in Sustainable Construction',
      excerptAr: 'تستثمر شركة مكة البركة في تقنيات البناء المستدام لتقليل البصمة الكربونية وتحسين كفاءة الطاقة.',
      excerptEn: 'Mecca El Baraka For Construction invests in sustainable construction technologies to reduce carbon footprint and improve energy efficiency.',
      contentAr: 'تستثمر شركة مكة البركة في تقنيات البناء المستدام لتقليل البصمة الكربونية وتحسين كفاءة الطاقة. تشمل هذه التقنيات استخدام مواد بناء صديقة للبيئة، وأنظمة طاقة شمسية متطورة، وتقنيات عزل حراري حديثة. تم تطبيق هذه التقنيات في عدة مشاريع تجريبية أثبتت نجاحها في تقليل استهلاك الطاقة بنسبة تصل إلى 40% مقارنة بالمباني التقليدية.',
      contentEn: 'Mecca El Baraka For Construction invests in sustainable construction technologies to reduce carbon footprint and improve energy efficiency. These technologies include the use of environmentally friendly building materials, advanced solar energy systems, and modern thermal insulation technologies. These technologies have been applied in several pilot projects that have proven successful in reducing energy consumption by up to 40% compared to traditional buildings.',
      images: [
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
        'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80'
      ],
      dateAr: '10 مارس 2024',
      dateEn: 'March 10, 2024',
      authorAr: 'أحمد محمد',
      authorEn: 'Ahmed Mohamed',
      views: 890,
      categoryAr: 'تقنيات',
      categoryEn: 'Technology',
      readTimeAr: '7 دقائق قراءة',
      readTimeEn: '7 min read',
      tagsAr: ['بناء مستدام', 'تقنيات خضراء', 'كفاءة الطاقة'],
      tagsEn: ['Sustainable Building', 'Green Technologies', 'Energy Efficiency']
    },
    {
      id: 3,
      titleAr: 'حصول الشركة على جائزة التميز في البناء',
      titleEn: 'Company Receives Excellence Award in Construction',
      excerptAr: 'حصلت الشركة على جائزة أفضل شركة مقاولات في مصر لعام 2024 تقديراً لتميزها.',
      excerptEn: 'The company received the award for the best contracting company in Egypt for 2024 in recognition of its excellence.',
      contentAr: 'حصلت الشركة على جائزة أفضل شركة مقاولات في مصر لعام 2024 تقديراً لتميزها في تنفيذ المشاريع وجودة العمل. تم تقييم الشركة بناءً على عدة معايير تشمل جودة التنفيذ، الالتزام بالمواعيد، الابتكار في التقنيات المستخدمة، والمسؤولية الاجتماعية. هذه الجائزة تعكس التزام الشركة المستمر بالتميز والابتكار في مجال البناء والتشييد.',
      contentEn: 'The company received the award for the best contracting company in Egypt for 2024 in recognition of its excellence in project execution and work quality. The company was evaluated based on several criteria including execution quality, adherence to deadlines, innovation in technologies used, and social responsibility. This award reflects the company\'s continued commitment to excellence and innovation in the field of construction and contracting.',
      images: [
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
        'https://images.unsplash.com/photo-1545454675-3531b543be5d?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80'
      ],
      dateAr: '5 مارس 2024',
      dateEn: 'March 5, 2024',
      authorAr: 'أحمد محمد',
      authorEn: 'Ahmed Mohamed',
      views: 750,
      categoryAr: 'جوائز',
      categoryEn: 'Awards',
      readTimeAr: '4 دقائق قراءة',
      readTimeEn: '4 min read',
      tagsAr: ['جوائز التميز', '2024', 'أفضل شركة'],
      tagsEn: ['Excellence Awards', '2024', 'Best Company']
    }
  ];

  const handleArticleHover = (article: any) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setSelectedArticle(article);
      setIsModalOpen(true);
    }, 2000); // 2 seconds
  };

  const handleArticleLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleArticleClick = (article: any) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setSelectedArticle(article);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
  };

  // Article Modal Component
  const ArticleModal: React.FC<{
    article: any;
    isOpen: boolean;
    onClose: () => void;
    language: 'ar' | 'en';
  }> = ({ article, isOpen, onClose, language }) => {
    const isRTL = language === 'ar';
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
      
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [isOpen]);

    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % article.images.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + article.images.length) % article.images.length);
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-20 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
              >
                <FaTimes className="w-5 h-5 text-gray-600" />
              </button>

              {/* Article Image Gallery */}
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img
                  src={article.images[currentImageIndex]}
                  alt={`${isRTL ? article.titleAr : article.titleEn} - ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Navigation Arrows */}
                {article.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 z-10"
                    >
                      <FaChevronLeft className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 z-10"
                    >
                      <FaChevronRight className="w-4 h-4 text-gray-700" />
                    </button>
                  </>
                )}
                
                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {article.images.length}
                </div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-800">
                    {isRTL ? article.categoryAr : article.categoryEn}
                  </span>
                </div>
              </div>

              {/* Article Content */}
              <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(90vh-320px)]">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {isRTL ? article.titleAr : article.titleEn}
                </h2>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {isRTL ? article.excerptAr : article.excerptEn}
                </p>

                {/* Article Stats */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="flex justify-center mb-2">
                      <FaNewspaper className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="text-sm font-semibold text-blue-600 mb-1">
                      {language === 'ar' ? 'التصنيف' : 'Category'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {isRTL ? article.categoryAr : article.categoryEn}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="flex justify-center mb-2">
                      <FaClock className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="text-sm font-semibold text-green-600 mb-1">
                      {language === 'ar' ? 'وقت القراءة' : 'Read Time'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {isRTL ? article.readTimeAr : article.readTimeEn}
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="flex justify-center mb-2">
                      <FaUserTie className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="text-sm font-semibold text-purple-600 mb-1">
                      {language === 'ar' ? 'الكاتب' : 'Author'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {isRTL ? article.authorAr : article.authorEn}
                    </div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                                         <div className="flex justify-center mb-2">
                       <FaEye className="w-6 h-6 text-orange-600" />
                     </div>
                    <div className="text-sm font-semibold text-orange-600 mb-1">
                      {language === 'ar' ? 'المشاهدات' : 'Views'}
                    </div>
                    <div className="text-xs text-gray-600">
                      {article.views.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {language === 'ar' ? 'محتوى المقال' : 'Article Content'}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {isRTL ? article.contentAr : article.contentEn}
                  </p>
                </div>

                {/* Tags */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {language === 'ar' ? 'العلامات' : 'Tags'}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(isRTL ? article.tagsAr : article.tagsEn).map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={onClose}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                  <button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FaExternalLinkAlt className="w-4 h-4" />
                    {language === 'ar' ? 'قراءة المقال كاملاً' : 'Read Full Article'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <section className={`section-padding bg-white ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4"
          >
            {language === 'ar' ? 'آخر الأخبار' : 'Latest News'}
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
          >
            {language === 'ar' ? 'آخر' : 'Latest'}
            <span className="text-gradient"> {language === 'ar' ? 'الأخبار والتحديثات' : 'News & Updates'}</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            {language === 'ar'
              ? 'تابع آخر الأخبار والتحديثات حول مشاريعنا وإنجازاتنا في مجال البناء والتشييد'
              : 'Follow the latest news and updates about our projects and achievements in construction and contracting'}
          </motion.p>
        </motion.div>

        {/* News Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {articles.map((article, index) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group cursor-pointer"
              onMouseEnter={() => handleArticleHover(article)}
              onMouseLeave={handleArticleLeave}
              onClick={() => handleArticleClick(article)}
            >
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 card-hover">
                {onDeleteArticle && (
                  <DeleteButton onDelete={() => onDeleteArticle(index)} />
                )}
                
                {/* Article Image */}
                <div className="relative overflow-hidden h-48">
                  <img
                    src={article.images[0]}
                    alt={isRTL ? article.titleAr : article.titleEn}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-800">
                      {isRTL ? article.categoryAr : article.categoryEn}
                    </span>
                  </div>
                </div>

                {/* Article Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {isRTL ? article.titleAr : article.titleEn}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                    {isRTL ? article.excerptAr : article.excerptEn}
                  </p>

                  {/* Article Meta */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <FaCalendarAlt className="w-4 h-4" />
                      <span>{isRTL ? article.dateAr : article.dateEn}</span>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <FaEye className="w-4 h-4" />
                      <span>{article.views}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-gray-500">
                      <FaUser className="w-4 h-4" />
                      <span>{isRTL ? article.authorAr : article.authorEn}</span>
                    </div>

                    {/* Read More Link */}
                    <div className="inline-flex items-center space-x-2 rtl:space-x-reverse text-primary-600 hover:text-primary-700 font-semibold transition-colors group/link">
                      <span>{language === 'ar' ? 'اقرأ المزيد' : 'Read More'}</span>
                      <FaArrowLeft className="w-4 h-4 group-hover/link:-translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All News CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to="/news"
            className="btn-outline text-lg px-8 py-4"
          >
            {language === 'ar' ? 'عرض جميع الأخبار' : 'View All News'}
          </Link>
        </motion.div>
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <ArticleModal
          article={selectedArticle}
          isOpen={isModalOpen}
          onClose={closeModal}
          language={language}
        />
      )}
    </section>
  );
};

export default NewsSection; 